import { useMemo } from 'react'

import {
  OptionsRegistrationDetails,
  RegistrationDetails,
  SubEventType,
  SubEvents,
  RegisterParticipants,
  Sessions,
  OptionType,
  SubEventOption,
  SessionOption,
  OptionPathKeys,
} from '../../../../types'

export type RegistrationOptions = OptionsRegistrationDetails & {
  start: string
  end: string
  subEventType: SubEventType
  registered?: RegisterParticipants['participant_options'][number]
  value?: string
}

const isSubEventOption = (
  elem: OptionsRegistrationDetails
): elem is Omit<OptionsRegistrationDetails, 'option'> & { option: SubEventOption } =>
  elem.option.type === OptionType.SubEventOption

const isSessionOption = (
  elem: OptionsRegistrationDetails
): elem is Omit<OptionsRegistrationDetails, 'option'> & { option: SessionOption } =>
  elem.option.type === OptionType.SessionOption

export const useFilterRegistrationOptions = (
  registrationDetails: RegistrationDetails | null | undefined,
  subEvents: SubEvents[] | undefined,
  sessions: Sessions[] | undefined,
  setOptionsByType?: (path: OptionPathKeys, value: string, elem: RegistrationOptions) => void
): Record<OptionPathKeys, RegistrationOptions[]> => {
  const registrationOptions: Record<OptionPathKeys, RegistrationOptions[]> = useMemo(() => {
    const sessionOptions = [] as RegistrationOptions[]
    const mealOptions = [] as RegistrationOptions[]
    const hotelOptions = [] as RegistrationOptions[]
    const workshopOptions = [] as RegistrationOptions[]
    const subEventOptions = [] as RegistrationOptions[]
    const textOptions = [] as RegistrationOptions[]
    const numberOptions = [] as RegistrationOptions[]
    const checkboxOptions = [] as RegistrationOptions[]

    registrationDetails?.options.forEach(elem => {
      let findSubEvent: SubEvents | undefined = undefined
      let findSession: Sessions | undefined = undefined

      if (isSubEventOption(elem)) {
        findSubEvent = subEvents?.find(subEl => subEl.id === elem.option.sub_event)
      }
      if (isSessionOption(elem)) {
        findSession = sessions?.find(sessEl => sessEl.id === elem.option.session)
      }

      const registered = registrationDetails?.existing_registration?.participant_options.find(
        e => e.option.id === elem.option.id
      )
      const opts = {
        ...elem,
        start: findSubEvent ? findSubEvent?.start : findSession?.start,
        end: findSubEvent ? findSubEvent?.end : findSession?.end,
        subEventType: findSubEvent?.sub_event_type,
        registered,
      } as RegistrationOptions

      if (findSubEvent) {
        switch (findSubEvent?.sub_event_type) {
          case SubEventType.MealSubEvent:
            if (registered && setOptionsByType) setOptionsByType('mealOptions', 'true', opts)
            mealOptions.push(opts)
            break
          case SubEventType.HotelSubEvent:
            if (registered && setOptionsByType) setOptionsByType('hotelOptions', 'true', opts)
            hotelOptions.push(opts)
            break
          case SubEventType.WorkShopSubEvent:
            if (registered && setOptionsByType) setOptionsByType('workshopOptions', 'true', opts)
            workshopOptions.push(opts)
            break
          default:
            if (registered && setOptionsByType) setOptionsByType('workshopOptions', 'true', opts)
            subEventOptions.push(opts)
            break
        }
      } else if (findSession) {
        if (registered && setOptionsByType) setOptionsByType('sessionOptions', 'true', opts)
        sessionOptions.push(opts)
      } else {
        switch (elem.option.type) {
          case OptionType.TextOption:
            if (registered && setOptionsByType)
              setOptionsByType('textOptions', registered.value, opts)
            textOptions.push(opts)
            break
          case OptionType.NumberOption:
            if (registered && setOptionsByType)
              setOptionsByType('numberOptions', registered.value, opts)
            numberOptions.push(opts)
            break
          case OptionType.CheckBoxOption:
            if (registered?.value === 'true' && setOptionsByType)
              setOptionsByType('checkboxOptions', 'true', opts)
            checkboxOptions.push(opts)
            break
          default:
            break
        }
      }
    })

    return {
      mealOptions,
      hotelOptions,
      workshopOptions,
      subEventOptions,
      sessionOptions,
      textOptions,
      numberOptions,
      checkboxOptions,
    }
  }, [
    registrationDetails?.options,
    registrationDetails?.existing_registration?.participant_options,
    subEvents,
    sessions,
    setOptionsByType,
  ])

  return registrationOptions
}
